import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks/reduxHooks';
import { Button, Box } from '@mui/material';
import {
  TCardType,
  ECardTypes,
  TUserRole,
  EUserRole,
  TCardMainButtonInfo,
} from 'shared/types/card.interface';
import { useGetProfileQuery } from 'redux/api/user.api';
import { TChallengeStatus, EChallengeStatus } from 'shared/types/challenges.interface';
import { openCurrentModal } from 'redux/slices/modals/modals.slice';
import { selectIsAuth } from 'redux/selectors/selectors';
import { AUTH_MODAL_DATA } from 'shared/constants/modal-constants';
import { CHALLENGE_CARD_BUTTONS, DEBATE_CARD_BUTTONS } from 'shared/constants/buttons-constants';
import { urls } from 'shared/constants/routing.constants';
import { LOCAL_STORAGE } from 'shared/constants/local-storage-constants';
import { EModalsNames } from 'shared/types/modal.interface';
import { useSetOpponentMutation } from 'redux/api/challenge.api';
import { useLoading } from 'shared/hooks/useLoading';
import { TDebateStatus, EDebateStatuses, IDebateData } from 'shared/types/debates.interface';
import { HoverPrompt } from 'components/HoverPrompt/HoverPrompt';
import { getDebateInfoFromCard } from 'redux/slices/zoom/zoom.slice';
import { useCheckDebateUserMutation } from 'redux/api/debate.api';
import { useMainButtonHover } from 'shared/hooks/useMainButtonHover';

interface ICardMainButtonProps {
  type: TCardType;
  userRole: TUserRole;
  status: TChallengeStatus | TDebateStatus;
  challengeId: number;
  debate?: IDebateData;
}

type TButtonInfo = {
  [M in TCardType]: {
    [K in TUserRole]: TCardMainButtonInfo | null;
  };
};

export const CardMainButton: React.FunctionComponent<ICardMainButtonProps> = ({
  type,
  userRole,
  status,
  challengeId,
  debate,
}) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const isAuth = useAppSelector(selectIsAuth);
  const location = useLocation().pathname;
  const { data: profileData = null, isLoading: isAuthLoading } = useGetProfileQuery(undefined, {
    skip: !isAuth,
  });
  const [checkDebateUser] = useCheckDebateUserMutation();
  const [setOpponent, { isLoading, data: newDebate }] = useSetOpponentMutation();
  useLoading(isLoading);

  const { isPromptVisible, checkOnHover } = useMainButtonHover(type, status);

  const handleStartDebate = useCallback(() => {
    dispatch(
      getDebateInfoFromCard({
        topic: String(debate?.id),
        userIdentity: `${profileData?.first_name} ${profileData?.last_name}`,
        name: String(profileData?.id),
      }),
    );
    checkDebateUser(debate?.id);
    localStorage.setItem(LOCAL_STORAGE.currentDebateId, String(debate.id));
    navigate(`${urls.myPages.video}/${debate?.id}`);
  }, [
    dispatch,
    debate?.id,
    profileData?.first_name,
    profileData?.last_name,
    profileData?.id,
    checkDebateUser,
    navigate,
  ]);

  const handleWatchDebate = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE.currentDebateId, String(debate.id));
    navigate(`${urls.commonPages.stream}/${debate?.id}`);
  }, [debate, navigate]);

  const debateIsStarted = useMemo(() => {
    if (debate?.debate_schedule.initiator_entry || debate?.debate_schedule.opponent_entry) {
      return true;
    }
    return false;
  }, [debate]);

  const buttonInfo: TButtonInfo = {
    [ECardTypes.Challenge]: {
      [EUserRole.Initiator]: null,
      [EUserRole.Opponent]: null,
      [EUserRole.RegularUser]:
        status === EChallengeStatus.Waiting
          ? {
              text: CHALLENGE_CARD_BUTTONS.mainButton.regularUser,
              onClick: profileData
                ? () => {
                    setOpponent(challengeId);
                  }
                : () => {
                    localStorage.setItem(LOCAL_STORAGE.currentChallengeId, String(challengeId));
                    dispatch(
                      openCurrentModal({
                        popupName: EModalsNames.AuthModal,
                        data: { subtitle: AUTH_MODAL_DATA.subtitleOnOpponent },
                      }),
                    );
                  },
            }
          : null,
    },
    [ECardTypes.Debate]: {
      [EUserRole.Initiator]:
        status === EDebateStatuses.Scheduled || status === EDebateStatuses.InProgress
          ? {
              text: debateIsStarted
                ? DEBATE_CARD_BUTTONS.mainButton.joinDebate
                : DEBATE_CARD_BUTTONS.mainButton.startDebate,
              onClick: handleStartDebate,
            }
          : null,
      [EUserRole.Opponent]:
        status === EDebateStatuses.Scheduled || status === EDebateStatuses.InProgress
          ? {
              text: debateIsStarted
                ? DEBATE_CARD_BUTTONS.mainButton.joinDebate
                : DEBATE_CARD_BUTTONS.mainButton.startDebate,
              onClick: handleStartDebate,
            }
          : null,
      [EUserRole.RegularUser]:
        status === EDebateStatuses.Scheduled || status === EDebateStatuses.InProgress
          ? { text: DEBATE_CARD_BUTTONS.mainButton.watchDebate, onClick: handleWatchDebate }
          : null,
    },
  };

  useEffect(() => {
    if (newDebate) navigate(`${urls.commonPages.debateCard}/${newDebate.debate_id}`);
  }, [newDebate, navigate]);

  useEffect(() => {
    if (
      location.startsWith(urls.commonPages.debateCard) &&
      debate?.status === EDebateStatuses.InProgress &&
      !isAuthLoading
    ) {
      if (userRole === EUserRole.Initiator || userRole === EUserRole.Opponent) {
        handleStartDebate();
      } else {
        handleWatchDebate();
      }
    }
  }, [location, debate, userRole, handleStartDebate, handleWatchDebate, isAuthLoading]);

  if (!buttonInfo[type][userRole]) return null;

  return (
    <Box onMouseEnter={checkOnHover} onMouseLeave={checkOnHover} width="100%">
      <Button
        variant="maxSize"
        className="button"
        disabled={type === ECardTypes.Debate && status === EDebateStatuses.Scheduled}
        onClick={buttonInfo[type][userRole].onClick}
        sx={{
          lineHeight: '26px',
          position: 'relative',
        }}
      >
        {buttonInfo[type][userRole].text}
        {type === ECardTypes.Debate && status === EDebateStatuses.Scheduled && (
          <HoverPrompt
            text={DEBATE_CARD_BUTTONS.mainButtonHoverPrompt}
            opacity={isPromptVisible ? 1 : 0}
          />
        )}
      </Button>
    </Box>
  );
};
